import React, { useState, useEffect } from "react";
import "./Calculator.css";
import {
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  OutlinedInput,
  InputAdornment,
} from "@mui/material";
import Heading from "./Heading/Heading";
import {
  formControlStyle,
  outlineInputStyle,
  resultLabelStyle,
} from "../../calculatorStyle";

const Calculator = () => {
  const [monthlyIncome, setMonthlyIncome] = useState(10000);
  const [additionalIncome, setAdditionalIncome] = useState(); //Optional
  const [existingEmis, setExistingEmis] = useState(2000);
  const [interestRate, setInterestRate] = useState(8); // Default interest rate
  const [loanTenure, setLoanTenure] = useState(2);
  const [tenureType, setTenureType] = useState("years"); // Options: 'months', 'years'
  const [processingFee, setProcessingFee] = useState();
  const [creditScore, setCreditScore] = useState("");
  const [maxLoanAmountResult, setMaxLoanAmountResult] = useState("");
  const [estimatedEMIResult, setEstimatedEMIResult] = useState("");
  const [totalInterestResult, setTotalInterestResult] = useState("");
  const [totalPaymentsResult, setTotalPaymentsResult] = useState("");

  const [maxLoanAmt, setMaxLoanAmt] = useState("");

  // Calculation Logic

  useEffect(() => {
    const CalculateLogic = () => {
      const tenureInMonths =
        tenureType === "years" ? loanTenure * 12 : loanTenure; // Convert years to months if needed
      const totalIncome =
        parseFloat(monthlyIncome) + parseFloat(additionalIncome || 0);
      const affordableEMI = totalIncome * 0.75 - parseFloat(existingEmis || 0);

      // Adjust interest rate based on credit score
      let adjustedInterestRate = interestRate;
      if (creditScore) {
        if (creditScore < 600) adjustedInterestRate += 2; // Poor credit
        else if (creditScore >= 750) adjustedInterestRate -= 1; // Excellent credit
      }

      // Calculate monthly interest rate
      const monthlyRate = adjustedInterestRate / 12 / 100;

      // Recommended Loan Amount
      const maxLoanAmount =
        (affordableEMI * (1 - Math.pow(1 + monthlyRate, -tenureInMonths))) /
        monthlyRate;

      // Estimated EMI
      const estimatedEMI =
        maxLoanAmount *
        (monthlyRate / (1 - Math.pow(1 + monthlyRate, -tenureInMonths)));

      // Total Interest Paid
      const totalInterest = estimatedEMI * tenureInMonths - maxLoanAmount;

      // Processing Fee (if provided)
      const processingFeeAmount = processingFee
        ? (maxLoanAmount * parseFloat(processingFee)) / 100
        : 0;

      // Total Loan Payments
      const totalPayments = maxLoanAmount + totalInterest + processingFeeAmount;

      // Format Results
      const formatCurrency = (value) => {
        return isNaN(value)
          ? "₹ 0"
          : `₹ ${new Intl.NumberFormat("en-IN").format(Math.round(value))}`;
      };
      setMaxLoanAmt(maxLoanAmount);
      setMaxLoanAmountResult(formatCurrency(maxLoanAmount));
      setEstimatedEMIResult(formatCurrency(estimatedEMI));
      setTotalInterestResult(formatCurrency(totalInterest));
      setTotalPaymentsResult(formatCurrency(totalPayments));
    };

    CalculateLogic();
  }, [
    monthlyIncome,
    additionalIncome,
    existingEmis,
    interestRate,
    loanTenure,
    tenureType,
    creditScore,
    processingFee,
  ]);

  const formatter = (value) => {
    return value.toLocaleString("en-IN");
  };

  const handleAmountChange = (e, setter) => {
    const value = e.target.value.replace(/,/g, ""); // Remove commas
    if (/^\d*\.?\d*$/.test(value)) {
      const numericValue = Number(value); // Convert to number
      if (numericValue <= 100000000) {
        setter(numericValue);
      }
    }
  };

  return (
    <>
      <div className="mortgage_calculator__container  ">
        <div className="mortgage_calculator_content">
          <div className="">
            <Heading />
            <div className="curren__new_loan_fields">
              <div className="current__loan mt-4">
                <div className="d-flex flex-column mt-3">
                  <div className="d-flex flex-column flex-md-row ">
                    <div className="details_box px-2">
                      <p
                        style={{ color: "#1f1f1f" }}
                        className="loan_details__label fw-bold archivo mb-1"
                      >
                        Monthly Income (₹){" "}
                        <span style={{ color: "#8A8A8A" }}>(required)</span>:
                      </p>
                      <FormControl fullWidth sx={formControlStyle}>
                        <OutlinedInput
                          fullWidth
                          size="small"
                          sx={outlineInputStyle}
                          startAdornment={
                            <InputAdornment position="start">
                              <span className="archivo">&#8377;</span>
                            </InputAdornment>
                          }
                          value={formatter(monthlyIncome || 0)} // Display value with commas
                          onChange={(e) =>
                            handleAmountChange(e, setMonthlyIncome)
                          }
                        />
                      </FormControl>
                    </div>
                  </div>
                  <div className="d-flex flex-column flex-md-row mt-4">
                    <div className="details_box px-2 ">
                      <p
                        style={{ color: "#1f1f1f" }}
                        className="loan_details__label fw-bold archivo mb-1"
                      >
                        Additional Household Income (₹){" "}
                        <span style={{ color: "#8A8A8A" }}>(optional)</span>:
                      </p>
                      <FormControl fullWidth sx={formControlStyle}>
                        <OutlinedInput
                          fullWidth
                          size="small"
                          sx={outlineInputStyle}
                          startAdornment={
                            <InputAdornment position="start">
                              <span className="archivo">&#8377;</span>
                            </InputAdornment>
                          }
                          value={formatter(additionalIncome || 0)} // Display value with commas
                          onChange={(e) =>
                            handleAmountChange(e, setAdditionalIncome)
                          }
                        />
                      </FormControl>
                    </div>
                    <div className="details_box px-2">
                      <p
                        style={{ color: "#1f1f1f" }}
                        className="loan_details__label fw-bold archivo mb-1"
                      >
                        Existing EMIs/Debts (₹){" "}
                        <span style={{ color: "#8A8A8A" }}>(optional)</span>:
                      </p>
                      <FormControl fullWidth sx={formControlStyle}>
                        <OutlinedInput
                          fullWidth
                          size="small"
                          sx={outlineInputStyle}
                          startAdornment={
                            <InputAdornment position="start">
                              <span className="archivo">&#8377;</span>
                            </InputAdornment>
                          }
                          value={formatter(existingEmis || 0)} // Display value with commas
                          onChange={(e) =>
                            handleAmountChange(e, setExistingEmis)
                          }
                        />
                      </FormControl>
                    </div>
                  </div>

                  <div className="details_box px-2 mt-4">
                    <p
                      style={{ color: "#1f1f1f" }}
                      className="loan_details__label fw-bold archivo mb-1"
                    >
                      Interest Rate (%):
                    </p>
                    <FormControl fullWidth sx={formControlStyle}>
                      <OutlinedInput
                        fullWidth
                        size="small"
                        endAdornment={
                          <InputAdornment position="end">
                            <span className="archivo">%</span>
                          </InputAdornment>
                        }
                        sx={outlineInputStyle}
                        value={interestRate || 0}
                        // onChange={(e) => setInterestRate(e.target.value)}

                        onChange={(e) => {
                          let value = e.target.value.replace(/,/g, "");
                          if (/^\d*\.?\d*$/.test(value)) {
                            if (value === "") {
                              setInterestRate("");
                            } else {
                              setInterestRate(value.replace(/^0+/, "") || "0");
                            }
                          }
                        }}
                        onFocus={() =>
                          interestRate === "0" && setInterestRate("")
                        }
                        onBlur={() =>
                          setInterestRate(
                            interestRate === "" ? "0" : interestRate
                          )
                        }
                      />
                    </FormControl>
                  </div>
                  <div className="d-flex flex-column flex-md-row mt-4">
                    <div className="details_box px-2">
                      <p
                        style={{ color: "#1f1f1f" }}
                        className="loan_details__label fw-bold archivo mb-1"
                      >
                        Loan Tenure:
                      </p>
                      <FormControl fullWidth sx={formControlStyle}>
                        <OutlinedInput
                          fullWidth
                          size="small"
                          sx={outlineInputStyle}
                          value={loanTenure || 0}
                          // onChange={(e) => setLoanTenure(e.target.value)}
                          onChange={(e) => handleAmountChange(e, setLoanTenure)}
                        />
                      </FormControl>
                    </div>

                    <div className="details_box px-2">
                      <FormControl>
                        <RadioGroup
                          aria-labelledby="demo-radio-buttons-group-label"
                          defaultValue="years"
                          onChange={(e) => setTenureType(e.target.value)}
                        >
                          <FormControlLabel
                            value="months"
                            control={<Radio />}
                            label="Months"
                          />
                          <FormControlLabel
                            value="years"
                            control={<Radio />}
                            label="Years"
                          />
                        </RadioGroup>
                      </FormControl>
                    </div>
                  </div>
                  <div className="d-flex flex-column flex-md-row mt-4">
                    <div className="details_box px-2">
                      <p
                        style={{ color: "#1f1f1f" }}
                        className="loan_details__label fw-bold archivo mb-1"
                      >
                        Processing Fee (%){" "}
                        <span style={{ color: "#8A8A8A" }}>(optional)</span>:
                      </p>
                      <FormControl fullWidth sx={formControlStyle}>
                        <OutlinedInput
                          fullWidth
                          size="small"
                          sx={outlineInputStyle}
                          value={processingFee || 0}
                          // onChange={(e) => setProcessingFee(e.target.value)}

                          onChange={(e) => {
                            let value = e.target.value.replace(/,/g, "");
                            if (/^\d*\.?\d*$/.test(value)) {
                              if (value === "") {
                                setProcessingFee("");
                              } else {
                                setProcessingFee(
                                  value.replace(/^0+/, "") || "0"
                                );
                              }
                            }
                          }}
                          onFocus={() =>
                            processingFee === "0" && setProcessingFee("")
                          }
                          onBlur={() =>
                            setProcessingFee(
                              processingFee === "" ? "0" : processingFee
                            )
                          }
                        />
                      </FormControl>
                    </div>
                    <div className="details_box px-2">
                      <p
                        style={{ color: "#1f1f1f" }}
                        className="loan_details__label fw-bold archivo mb-1"
                      >
                        Credit Score{" "}
                        <span style={{ color: "#8A8A8A" }}>(optional)</span>:
                      </p>
                      <FormControl fullWidth sx={formControlStyle}>
                        <OutlinedInput
                          fullWidth
                          size="small"
                          sx={outlineInputStyle}
                          value={creditScore || 0}
                          // onChange={(e) => setCreditScore(e.target.value)}
                          onChange={(e) =>
                            handleAmountChange(e, setCreditScore)
                          }
                        />
                      </FormControl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="calculation_result__container">
              <div className="claculation_result__data mt-4">
                {maxLoanAmt <= 0 ? (
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 600,
                      letterSpacing: "0.08px",
                      color: "#ff3838",

                      fontFamily: "'Archivo',sans-serif",
                    }}
                  >
                    As per your given data you are not available for more loans.
                  </Typography>
                ) : (
                  <div className="positive-result d-flex flex-column gap-4 justify-content-between mt-3">
                    <div>
                      <Typography
                        sx={resultLabelStyle}
                        className="loan_details__label archivo mb-1"
                      >
                        Recommended Loan Amount:
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Archivo', sans-serif",
                          fontSize: "24px",
                          lineHeight: "24px",
                          letterSpacing: "normal",
                          textAlign: "start",
                          fontWeight: 700,
                          color: "#005FB9",
                        }}
                      >
                        {maxLoanAmountResult}
                      </Typography>
                    </div>
                    <div>
                      <Typography
                        sx={{
                          fontSize: 13,
                          lineHeight: "20px",
                          fontWeight: 600,
                          color: "#1f1f1f",
                          fontFamily: "'Archivo',sans-serif",
                        }}
                        className="loan_details__label archivo mb-1"
                      >
                        Estimated Monthly EMI:
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Archivo', sans-serif",
                          fontSize: "24px",
                          lineHeight: "24px",
                          letterSpacing: "normal",
                          textAlign: "start",
                          fontWeight: 700,
                          color: "#005FB9",
                        }}
                      >
                        {estimatedEMIResult}
                      </Typography>
                    </div>
                    <div>
                      <Typography
                        sx={{
                          fontSize: 13,
                          lineHeight: "20px",
                          fontWeight: 600,
                          color: "#1f1f1f",
                          fontFamily: "'Archivo',sans-serif",
                        }}
                        className="loan_details__label archivo mb-1"
                      >
                        Total Interest Paid:
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Archivo', sans-serif",
                          fontSize: "24px",
                          lineHeight: "24px",
                          letterSpacing: "normal",
                          textAlign: "start",
                          fontWeight: 700,
                          color: "#005FB9",
                        }}
                      >
                        {totalInterestResult}
                      </Typography>
                    </div>
                    <div>
                      <Typography
                        sx={{
                          fontSize: 13,
                          lineHeight: "20px",
                          fontWeight: 600,
                          color: "#1f1f1f",
                          fontFamily: "'Archivo',sans-serif",
                        }}
                        className="loan_details__label archivo mb-1"
                      >
                        Total Loan Payments:
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Archivo', sans-serif",
                          fontSize: "24px",
                          lineHeight: "24px",
                          letterSpacing: "normal",
                          textAlign: "start",
                          fontWeight: 700,
                          color: "#005FB9",
                        }}
                      >
                        {totalPaymentsResult}
                      </Typography>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Calculator;
